import { getConversationById } from 'api/conversation'
import classnames from 'classnames'
import ChatLoader from 'components/ChatLoader'
import Composer, { ComposerState } from 'components/Composer'
import ConversationHistory from 'components/ConversationHistory'
import Footer from 'components/Footer'
import LoaderBalls from 'components/LoaderBalls'
import MessageGroupList from 'components/MessageGroupList'
import RateConversation from 'components/RateConversation'
import ThemeStyleSheets from 'components/ThemeStyleSheets'
import { NEW_CONVERSATION_ID } from 'constants/conversations'
import { CHAT_TAKEOVER_CLASSNAME } from 'constants/frames'
import useConversationHistoryToggle from 'hooks/useConversationHistoryToggle'
import useTimedMessages from 'hooks/usedTimedMessages'
import useFetchActiveConversationMessages from 'hooks/useFetchActiveConversationMessages'
import useHasFooter from 'hooks/useHasFooter'
import useLanguageSwitcher from 'hooks/useLanguageSwitcher'
import usePrevious from 'hooks/usePrevious'
import useReflowFrameClassName from 'hooks/useReflowFrame'
import useResolvePrivacyPolicy from 'hooks/useResolvePrivacyPolicy'
import useThemeColors from 'hooks/useThemeColors'
import HeaderLayout from 'layouts/Header'
import { storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { createConversationEvent } from 'modules/conversations'
import { receiveConversation, sendMessage } from 'modules/conversations/actions'
import { isPrerenderedConversation } from 'modules/conversations/utils'
import { getEmbedConfiguration } from 'modules/embed/selectors'
import { ERROR } from 'modules/logging/errors'
import {
  groupMessages,
  isButtonsOnlyMessage,
  isEndUserMessage,
  isMeetingScheduleMessage,
  isRateConversationCTAMessage,
  messageContainsButtons,
  toVisibleMessagesArray
} from 'modules/message/utils'
import { measureTime } from 'modules/metrics/utils'
import { last, nth, pathOr } from 'ramda'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { LiveAnnouncer } from 'react-aria-live'
import { useDispatch, useSelector } from 'react-redux'
import { isMobileDevice } from 'shared/src/utils/platform'
import { CampaignDisplayType } from 'types/campaigns'
import { ConversationEventType, ConversationId } from 'types/conversation'
import { Message } from 'types/message'
import { error, log } from 'utils'
import { initI18n } from 'utils/i18n'
import { broadcast } from 'utils/message'
import { timingMetric } from 'utils/metrics'
import { stopTraceMark } from 'utils/trace'

import './style.css'

let reflowTimeout = null

const ChatLayout = () => {
  const dispatch = useDispatch()
  const isMobile = isMobileDevice()
  const reflowFrameCX = useReflowFrameClassName()

  const [reflowHidden, setReflowHidden] = useState(true)
  useLanguageSwitcher()

  const sendSubmittedEventForChatResponseCampaign = () => {
    if (!activeCampaign) {
      return
    }

    const hasChatCta = pathOr(null, ['attributes', 'cta', 'CtaType'], activeCampaign) === "CHAT_RESPONSE"
    if (hasChatCta) {
      createConversationEvent(ConversationEventType.SUBMITTED)
    }
  }
  const { chatOpen: open, isChatTakeover } = useSelector(state => state.view)

  const { showBranding = false } = useSelector(getEmbedConfiguration)
  const { noPolicy } = useResolvePrivacyPolicy()
  const noFooterContent = !showBranding && noPolicy

  useLayoutEffect(() => {
    if (!(isChatTakeover && open)) {
      return
    }

    broadcast({
      topic: 'set-frame-width-and-height',
      message: {
        className: CHAT_TAKEOVER_CLASSNAME,
        height: '100%',
        max: true,
        name: 'chat',
        width: '100%'
      }
    })
  }, [isChatTakeover, open])

  const doSendMessage = payload => {
    if ((isWelcomeMessageCampaign || isChatTakeover) && !activeConversationId) {
      const playbookId = pathOr(null, ['attributes', 'playbookId'], activeCampaign)

      sendSubmittedEventForChatResponseCampaign()
      storeAnnouncementCampaignCompleted(activeCampaign.id, playbookId)
    }
    dispatch(sendMessage(payload))
  }

  const activeConversationId: ConversationId = useSelector(state =>
    state.conversations.activeConversation
  )

  const { fetchMessages } = useFetchActiveConversationMessages()

  const lastActiveConversationId = usePrevious(activeConversationId)

  const storedConversations = useSelector(state => state.conversations.conversations)
  const storedMessages = useSelector(state => state.conversations.messages)

  const messages = useSelector(state =>
    pathOr({}, ['conversations', 'messages', state.conversations.activeConversation], state)
  )

  const isConversationPermaclosed = useSelector(state =>
    pathOr(false, ['conversations', 'conversationStatus', state.conversations.activeConversation, 'permaclosed'], state)
  )

  const messagesList = useMemo(() => toVisibleMessagesArray(messages), [messages])

  const activeCampaign = useSelector(state => (
    state.campaigns.activeCampaign
  ))

  const isWelcomeMessageCampaign = useMemo(() => {
    return pathOr(null, ['attributes', 'campaignDisplayType'], activeCampaign) === CampaignDisplayType.WELCOME_MESSAGE
  }, [activeCampaign])

  const { hasConsent } = useSelector(state => (
    state.session.gdpr
  ))

  const endUserResponded = useSelector(state =>
    pathOr(false, ['conversations', 'conversationStatus', activeConversationId, 'endUserResponded'], state)
  )

  const { conversationHistoryOpen, conversationHistoryEnabled } = useConversationHistoryToggle()

  const { widgetBackgroundStyles } = useThemeColors()

  useEffect(() => {
    const fetchInfoForActiveConversation = async () => {
      const hasNullOrNewConversation = !activeConversationId || activeConversationId === NEW_CONVERSATION_ID
      const hasNotChangedActiveConversation = lastActiveConversationId === activeConversationId
      const hasAlreadyFetchedActiveConversation = !!storedConversations[activeConversationId] && !!storedMessages[activeConversationId]

      if (hasNullOrNewConversation ||
        hasNotChangedActiveConversation ||
        hasAlreadyFetchedActiveConversation ||
        isPrerenderedConversation(activeConversationId)) {
        return
      }

      try {
        const { data: conversation } = await getConversationById(activeConversationId)
        dispatch(receiveConversation({ conversation }))
        fetchMessages({})
      } catch (err) {
        error(ERROR.MESSAGE.FETCH_MESSAGE_OR_ACTIVE_CONVO, err, true)
      }
    }

    fetchInfoForActiveConversation()
  }, [activeConversationId, dispatch, fetchMessages, lastActiveConversationId, storedConversations, storedMessages])


  useEffect(() => {
    const delaySetReflowHidden = () => {
      setReflowHidden(true)
    }

    if (!open) {
      reflowTimeout = setTimeout(delaySetReflowHidden, 350)
    } else {
      const focus = async () => {
        await broadcast({
          topic: 'focus-frame',
          message: {
            name: 'chat'
          }
        })
      }
      focus()
      reflowTimeout && clearTimeout(reflowTimeout)
      setReflowHidden(false)
    }
  }, [open])

  const shouldRenderComposer = useMemo(() => {
    if (!hasConsent || !open) {
      return false
    }

    if (activeCampaign && activeCampaign.id && !activeConversationId) {
      const hasCta = !!pathOr(null, ['attributes', 'cta'], activeCampaign)
      const hasNonChatCta = pathOr(null, ['attributes', 'cta', 'CtaType'], activeCampaign) !== "CHAT_RESPONSE"

      if (hasCta && hasNonChatCta) {
        return false
      }
    }

    // If we only have up to 2 messages, check if the first message
    // is a buttonsOnly question skill. The second is a response to the question.
    // If this is true, don't render the composer until the visitor
    // gets the next set of messages.
    const isNotShowingFirstMessageWithButtonsOnly = (
      messagesList.length > 0 &&
      messagesList.length <= 2 &&
      !isButtonsOnlyMessage(messagesList[0])
    )

    return !isChatTakeover || isNotShowingFirstMessageWithButtonsOnly || messagesList.length > 2
  }, [activeCampaign, activeConversationId, hasConsent, isChatTakeover, messagesList, open])

  useEffect(() => {
    stopTraceMark('WIDGET_CHAT_FRAME.start')
  }, [])

  const postFormTakeoverLoggedRef = useRef<boolean>(false)

  useEffect(() => {
    if (postFormTakeoverLoggedRef.current) {
      return
    }

    if (isChatTakeover && messagesList.length > 0) {
      const tti = measureTime('post_form_takeover_tti')
      const show = measureTime('post_form_takeover_show')
      if (tti) {
        timingMetric('Post Form Takeover TTI', tti)
      }
      if (show) {
        timingMetric('Post Form Takeover Show', show)
      }
      postFormTakeoverLoggedRef.current = true
    }
  }, [isChatTakeover, messagesList])

  const composerState = useMemo<ComposerState>(() => {
    if (isConversationPermaclosed) {
      return ComposerState.UNAVAILABLE
    }

    const latestMessage = last(messagesList)

    if (!latestMessage) {
      return ComposerState.AVAILABLE
    }

    const secondToLastMessage = nth(-2, messagesList)
    const isLastMessageButtonsOnlyResponse = isButtonsOnlyMessage(secondToLastMessage) && isEndUserMessage(latestMessage)
    const isLatestMessageStrictCTA = (
      isButtonsOnlyMessage(latestMessage) ||
      isLastMessageButtonsOnlyResponse ||
      isMeetingScheduleMessage(latestMessage)
    )

    if (isLatestMessageStrictCTA) {
      return ComposerState.UNAVAILABLE_BUTTONS
    }

    if (messageContainsButtons(latestMessage) ||
       (messageContainsButtons(secondToLastMessage) && isEndUserMessage(latestMessage))
    ) {
      return ComposerState.AVAILABLE_BUTTONS
    }

    return ComposerState.AVAILABLE
  }, [messagesList, isConversationPermaclosed])

  const composerInputDisabled = composerState === ComposerState.AVAILABLE || composerState === ComposerState.AVAILABLE_BUTTONS

  const timedMessages = useTimedMessages(messagesList, activeConversationId)
  const messageGroups = useMemo(() => groupMessages(timedMessages), [timedMessages])

  const ChatHistoryList = useMemo(() => {
    return (
      <MessageGroupList
        messageGroups={messageGroups}
        messages={timedMessages}
        reflowHidden={reflowHidden}
        disabled={conversationHistoryOpen}
      />
    )
  }, [messageGroups, timedMessages, reflowHidden, conversationHistoryOpen])

  const hasFooter = useHasFooter()

  const rateConversationMessage: Message = useMemo(() => {
    if (timedMessages && timedMessages.length) {
      const lastMessage = last(timedMessages)
      if (!!lastMessage && isRateConversationCTAMessage(lastMessage)) {
        return lastMessage
      }
    }
    return null
  }, [timedMessages])

  const shouldShowConversation = hasConsent && (!!activeConversationId || isChatTakeover)
  const noComposer = !shouldRenderComposer || !composerInputDisabled

  return (
    <main
      className={classnames("drift-widget-chat-layout", {
        'drift-widget-chat-layout__takeover': isChatTakeover && !isMobile,
        'drift-widget-chat-layout__takeover--noResponse': isChatTakeover && !isMobile && !endUserResponded,
        [reflowFrameCX]: !!reflowFrameCX
      })}
      aria-live="polite"
    >
      <ThemeStyleSheets />
      <div
        className={classnames('drift-widget-chat-wrapper', {
          'drift-widget-chat-wrapper__open': open,
          'drift-widget-chat-wrapper__closed': !open,
          'drift-widget-chat-wrapper__reflow-hidden': reflowHidden,
          'drift-widget-chat-wrapper__active-conversation': shouldShowConversation,
          'drift-widget-chat-wrapper__no-footer-content' : noFooterContent && noComposer,
          'drift-widget-chat-wrapper__mobile': isMobile,
          'drift-widget-chat-wrapper__conversation-history': conversationHistoryOpen
        })}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <HeaderLayout chatHidden={reflowHidden} />

        {!isChatTakeover && conversationHistoryEnabled &&
          <ConversationHistory />
        }

        {shouldShowConversation && (
          <div
            className={classnames('drift-widget-message-history', {
              'drift-widget-message-history--with-footer': hasFooter,
              'drift-widget-message-history--no-composer': (noComposer)
            })}
            style={widgetBackgroundStyles}
          >
            {ChatHistoryList}
            {messagesList.length === 0 &&
              <LoaderBalls titleId={'ChatLayoutLoading'}/>
            }
          </div>
        )}

        <div className="drift-widget-chat-bottom">
          {shouldRenderComposer &&
            <Composer
              state={composerState}
              disabled={conversationHistoryOpen}
              onSubmit={doSendMessage}
              chatHidden={reflowHidden}
              activeConversationId={activeConversationId}
            />
          }
          {!conversationHistoryOpen && <Footer />}
        </div>

        {activeConversationId && !!rateConversationMessage &&
          <RateConversation message={rateConversationMessage} />
        }
      </div>
    </main>
  )
}


export default <P extends object>(props: P) => {
  const [i18nInitialized, setI18nInitialized] = useState(false)

  const initComplete = useSelector(state => (
    state.view.initComplete
  ))

  const locale = useSelector(state => (
    pathOr('en', ['embed', 'configuration', 'locale'], state)
  ))

  useEffect(() => {
    if (initComplete && !i18nInitialized) {
      const runAsync = async () => {
        try {
          await initI18n(locale)
        } catch (err) {
          log({ data: [`i18n failed to load - ${err['message']}`, err], type: 'warn', internal: true })
        }
        setI18nInitialized(true)
      }
      runAsync()
    }
  }, [initComplete, i18nInitialized, locale])

  return (
    <LiveAnnouncer>
      {(initComplete && i18nInitialized)
        ? <ChatLayout {...props} />
        : <ChatLoader />
      }
    </LiveAnnouncer>
  )
}
